import { IsNotEmpty, IsOptional, IsString } from 'class-validator';

/**
 * Response DTO for Reliable PBX connection status.
 *
 * Reliable PBX is a non-OAuth integration: the admin supplies a static API
 * Bearer token plus a single instance-wide caller-ID number, both stored
 * (encrypted) in the instance _secret_store. There are no tokens to refresh.
 */
export class ReliablePbxConnectionStatusDto {
	public connected!: boolean;
	/** The instance-wide outbound caller-ID number (shown for confirmation). */
	public callerId?: string;
	public connectedDate?: string;
	public connectedByUserName?: string;
}

/**
 * Request DTO for connecting (or reconnecting) Reliable PBX.
 *
 * Unlike RingCentral/Vonage there is no OAuth redirect — the admin enters the
 * credentials directly and we persist them. On reconnect the `bearerToken` may
 * be omitted/blank to keep the existing stored token (we can't return the
 * encrypted secret to pre-fill the form), updating only the caller ID. On an
 * initial connect the token is required (enforced in the service).
 */
export class ReliablePbxConnectRequestDto {
	@IsOptional()
	@IsString()
	public bearerToken?: string;

	@IsString()
	@IsNotEmpty()
	public callerId!: string;
}

/**
 * Response DTO for user dial status (click-to-dial capability).
 *
 * `userExtension` is the calling user's `phoneExtensionNumber` — the desk
 * extension Reliable PBX rings first before connecting the destination. It is
 * assigned by an admin in the user editor; users cannot self-assign.
 */
export class ReliablePbxUserDialStatusDto {
	public connected!: boolean;
	public canDial!: boolean;
	public userExtension!: string | null;
}

/**
 * Request DTO for initiating a dial.
 */
export class ReliablePbxDialRequestDto {
	@IsString()
	@IsNotEmpty()
	public phoneNumber!: string;
}

/**
 * Response DTO for dial result.
 */
export class ReliablePbxDialResponseDto {
	public success!: boolean;
	/** The external call id returned by Reliable PBX (response.data.ID). */
	public externalId?: string;
	public error?: string;
	/** Whether the call was diverted to a different number. */
	public diverted?: boolean;
	/** The phone number the call was diverted to (E.164 format). */
	public divertedTo?: string;
	/** The original phone number that was requested. */
	public originalNumber?: string;
}

/**
 * Internal model for the Reliable PBX place-call API response.
 *
 * Reliable PBX returns HTTP 200 even for logical failures, signaling the real
 * outcome via the body's `status` flag (`1` = success, `0` = failure). On
 * failure the top-level `message` describes the problem (e.g. an invalid
 * caller ID); on success the call id is in `data.ID`.
 */
export interface ReliablePbxApiResponseModel {
	/** Outcome flag: 1 = success, 0 = failure. */
	status?: number;
	/** Top-level message — populated on failure. */
	message?: string;
	/** Present on success; contains the placed-call details. */
	data?: {
		Response?: string;
		Message?: string;
		message?: string;
		ID?: string;
	};
	/** Some responses include the call id at the top level. */
	ID?: string;
}

/**
 * Options for initiating a dial (used by client-side services).
 * Currently empty but added for future extensibility and API consistency.
 */
export type ReliablePbxDialOptionsModel = Record<string, never>;
